// IndexedDB persistence. Trees can be large (thousands of people), which rules out
// localStorage's ~5 MB quota. If IndexedDB is unavailable (some private-browsing modes),
// everything falls back to memory and the UI warns that changes won't be kept.
// Media files have a store of their own ('files', keyed by content hash) so that autosaving
// a tree never rewrites them; each tree's meta lists the files it uses, for clean-up.
// Small device-level values (device sync's pairings and certificate, deleted trees) live in 'kv'.

import type { Tree } from './types';

export interface TreeMeta {
  id: string;
  name: string;
  updatedAt: string;
  people: number;
  /** Keys of the media files the tree uses. */
  files?: string[];
}

const DB_NAME = 'relationships';
const DB_VERSION = 3;

let dbPromise: Promise<IDBDatabase> | null = null;
const memory = new Map<string, Tree>();
const memoryFiles = new Map<string, Blob>();
const memoryKv = new Map<string, unknown>();
let memoryOnly = false;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('trees')) db.createObjectStore('trees', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('files')) db.createObjectStore('files');
        if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
      };
      req.onsuccess = () => {
        // Let a newer version of the app in another tab upgrade the database.
        req.result.onversionchange = () => req.result.close();
        resolve(req.result);
      };
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error('Storage is blocked by another open tab.'));
    } catch (e) {
      reject(e);
    }
  });
  return dbPromise;
}

function tx<T>(stores: string[], mode: IDBTransactionMode, fn: (t: IDBTransaction) => IDBRequest<T> | void): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const t = db.transaction(stores, mode);
        const req = fn(t);
        t.oncomplete = () => resolve(req ? req.result : undefined);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error ?? new Error('Storage transaction aborted.'));
      }),
  );
}

export async function storageAvailable(): Promise<boolean> {
  try {
    await open();
    return true;
  } catch {
    memoryOnly = true;
    return false;
  }
}

export function isMemoryOnly(): boolean {
  return memoryOnly;
}

function metaOf(t: Tree): TreeMeta {
  const files = [...new Set(Object.values(t.media ?? {}).flatMap((m) => (m.file ? [m.file.key] : [])))];
  return { id: t.id, name: t.name, updatedAt: t.updatedAt, people: Object.keys(t.people).length, files };
}

export async function listTrees(): Promise<TreeMeta[]> {
  if (memoryOnly) return [...memory.values()].map(metaOf);
  const all = (await tx<TreeMeta[]>(['meta'], 'readonly', (t) => t.objectStore('meta').getAll())) ?? [];
  return all.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function loadTree(id: string): Promise<Tree | undefined> {
  if (memoryOnly) return memory.get(id);
  return tx<Tree>(['trees'], 'readonly', (t) => t.objectStore('trees').get(id));
}

export async function saveTree(tree: Tree): Promise<void> {
  if (memoryOnly) {
    memory.set(tree.id, tree);
    return;
  }
  await tx(['trees', 'meta'], 'readwrite', (t) => {
    t.objectStore('trees').put(tree);
    t.objectStore('meta').put(metaOf(tree));
  });
}

export async function deleteTree(id: string): Promise<void> {
  if (memoryOnly) {
    memory.delete(id);
    return;
  }
  await tx(['trees', 'meta'], 'readwrite', (t) => {
    t.objectStore('trees').delete(id);
    t.objectStore('meta').delete(id);
  });
}

// ---------------------------------------------------------------- media files

export async function putFiles(files: Map<string, Blob>): Promise<void> {
  if (!files.size) return;
  if (memoryOnly) {
    for (const [k, b] of files) memoryFiles.set(k, b);
    return;
  }
  await tx(['files'], 'readwrite', (t) => {
    const store = t.objectStore('files');
    for (const [k, b] of files) store.put(b, k);
  });
}

export async function getFile(key: string): Promise<Blob | undefined> {
  if (memoryOnly) return memoryFiles.get(key);
  return tx<Blob>(['files'], 'readonly', (t) => t.objectStore('files').get(key));
}

/** Keys of every stored media file. */
export async function listFileKeys(): Promise<string[]> {
  if (memoryOnly) return [...memoryFiles.keys()];
  return ((await tx<IDBValidKey[]>(['files'], 'readonly', (t) => t.objectStore('files').getAllKeys())) ?? []).map(String);
}

/**
 * Delete files no saved tree uses. Run at startup, never mid-session, so a file removed from a
 * tree is still there if the removal is undone.
 */
export async function removeUnusedFiles(): Promise<number> {
  if (memoryOnly) return 0;
  const metas = (await tx<TreeMeta[]>(['meta'], 'readonly', (t) => t.objectStore('meta').getAll())) ?? [];
  const used = new Set(metas.flatMap((m) => m.files ?? []));
  const keys = ((await tx<IDBValidKey[]>(['files'], 'readonly', (t) => t.objectStore('files').getAllKeys())) ?? []).map(String);
  const unused = keys.filter((k) => !used.has(k));
  if (unused.length) await tx(['files'], 'readwrite', (t) => {
    const store = t.objectStore('files');
    for (const k of unused) store.delete(k);
  });
  return unused.length;
}

// ---------------------------------------------------------------- device values

export async function getKv<T>(key: string): Promise<T | undefined> {
  if (memoryOnly) return memoryKv.get(key) as T | undefined;
  return tx<T>(['kv'], 'readonly', (t) => t.objectStore('kv').get(key));
}

/** Values can be anything IndexedDB can store, including an RTCCertificate. */
export async function setKv(key: string, value: unknown): Promise<void> {
  if (memoryOnly) {
    memoryKv.set(key, value);
    return;
  }
  await tx(['kv'], 'readwrite', (t) => {
    t.objectStore('kv').put(value, key);
  });
}

// ---------------------------------------------------------------- persistence

export type Persistence = 'granted' | 'not-granted' | 'unsupported';

/**
 * Whether the browser has promised to keep this site's storage. Without that, storage is "best
 * effort": the browser may clear it when the disk runs low, and Safari clears it after seven days
 * without a visit (unless the app was added to the home screen).
 */
export async function persistence(): Promise<Persistence> {
  if (memoryOnly || typeof navigator === 'undefined' || !navigator.storage?.persisted) return 'unsupported';
  try {
    return (await navigator.storage.persisted()) ? 'granted' : 'not-granted';
  } catch {
    return 'unsupported';
  }
}

/** Ask the browser to keep storage. Chrome and Safari decide on their own; Firefox asks the user. */
export async function requestPersistence(): Promise<Persistence> {
  if (memoryOnly || typeof navigator === 'undefined' || !navigator.storage?.persist) return 'unsupported';
  try {
    return (await navigator.storage.persist()) ? 'granted' : 'not-granted';
  } catch {
    return 'unsupported';
  }
}

export function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function lsSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage disabled; fine */
  }
}

export function lsRemove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage disabled; fine */
  }
}

export function lsKeys(): string[] {
  try {
    return Object.keys(localStorage);
  } catch {
    return [];
  }
}
